import { Logo } from "@/components/marketing/Navbar";
import { Link } from "react-router-dom";
import { useState } from "react";

export default function AuthShell({ title, subtitle, children, footer }) {
  return (
    <div className="flex min-h-screen bg-background">
      {/* Left brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between bg-navy p-12 lg:flex">
        <Logo light />
        <div>
          <h2 className="font-heading text-3xl font-semibold leading-tight text-white">
            Save Time. Show Return.
          </h2>
          <p className="mt-4 max-w-md text-base leading-relaxed text-slate-300">
            Track or import AI-assisted work, compare it to the time it would have taken without AI, and
            know what to expand, review, improve, or stop.
          </p>
        </div>
        <div className="space-y-2 text-sm text-slate-400">
          <p>“NetLift turns AI use into decision-grade financial evidence.”</p>
          <p>AI Value Management · netlift.app</p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex w-full flex-col justify-center px-6 py-12 sm:px-12 lg:w-1/2">
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <Logo />
          </div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">{title}</h1>
          {subtitle && <p className="mt-2 text-sm text-slate-500">{subtitle}</p>}
          <div className="mt-8">{children}</div>
          {footer && <div className="mt-6 text-sm text-slate-500">{footer}</div>}
        </div>
      </div>
    </div>
  );
}

export function GoogleButton({ label = "Continue with Google" }) {
  const handle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button
      type="button"
      onClick={handle}
      className="flex w-full items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
      data-testid="google-login-btn"
    >
      <svg className="h-4 w-4" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
        <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z" />
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.46 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z" />
      </svg>
      {label}
    </button>
  );
}

export function MicrosoftButton({ label = "Continue with Microsoft" }) {
  const [busy, setBusy] = useState(false);

  const handle = async () => {
    setBusy(true);
    try {
      const { getMsal } = await import("@/lib/msal");
      const msal = await getMsal();
      // complete/clear any pending redirect state before starting a new one
      await msal.handleRedirectPromise({ navigateToLoginRequestUrl: false }).catch(() => {});
      try {
        await msal.loginRedirect({ scopes: ["openid", "profile", "email"], prompt: "select_account" });
      } catch (e) {
        if (e?.errorCode === "interaction_in_progress") {
          // stale flag from an interrupted attempt — clear MSAL state and retry once
          Object.keys(sessionStorage)
            .filter((k) => k.toLowerCase().includes("msal"))
            .forEach((k) => sessionStorage.removeItem(k));
          await msal.loginRedirect({ scopes: ["openid", "profile", "email"], prompt: "select_account" });
        } else {
          throw e;
        }
      }
    } catch (e) {
      console.error("Microsoft sign-in failed", e);
      const detail = e?.errorMessage || e?.message || "";
      alert(`Microsoft sign-in failed. ${detail}`.trim());
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handle}
      disabled={busy}
      className="flex w-full items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-60"
      data-testid="microsoft-login-btn"
    >
      <svg className="h-4 w-4" viewBox="0 0 23 23">
        <rect x="1" y="1" width="10" height="10" fill="#F25022" />
        <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
        <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
        <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
      </svg>
      {busy ? "Signing in…" : label}
    </button>
  );
}
